import { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import {
    Box,
    Typography,
    Button,
    Stack,
    Chip,
    Tooltip,
    IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GenericDataTable from "../components/GenericDataTable";
import BackupTypeModal from "../components/BackupTypeModal";
import { getBackupTypes, createBackupType, updateBackupType, deleteBackupType } from "../Services/backupTypeService";
import { useToast } from "../context/ToastContext";

export default function BackupTypePage() {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [saving, setSaving] = useState(false);
    const { showToast } = useToast();
    const showToastRef = useRef(showToast);
    useEffect(() => { showToastRef.current = showToast; }, [showToast]);

    useEffect(() => {
        let isMounted = true;
        async function loadTypes() {
            setLoading(true);
            try {
                const data = await getBackupTypes();
                if (isMounted) setTypes(data);
            } catch {
                if (isMounted) showToastRef.current("Erreur lors du chargement des types", { severity: "error" });
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        loadTypes();
        return () => { isMounted = false; };
    }, []);

    const handleOpenCreate = () => { setEditData(null); setModalOpen(true); };
    const handleOpenEdit = (type) => { setEditData(type); setModalOpen(true); };
    const handleClose = () => { setModalOpen(false); setEditData(null); };

    const handleSave = async (formData) => {
        setSaving(true);
        try {
            if (editData) {
                const updated = await updateBackupType(editData.id, formData);
                setTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
                showToast("Type mis à jour", { severity: "success" });
            } else {
                const created = await createBackupType(formData);
                setTypes((prev) => [...prev, created]);
                showToast("Type créé", { severity: "success" });
            }
            handleClose();
        } catch (e) {
            const msg = e.response?.data?.message || (editData ? "Erreur lors de la mise à jour" : "Erreur lors de la création");
            showToast(msg, { severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (type) => {
        if (!window.confirm(`Supprimer le type "${type.name}" ?`)) return;
        try {
            await deleteBackupType(type.id);
            setTypes((prev) => prev.filter((t) => t.id !== type.id));
            showToast("Type supprimé", { severity: "success" });
        } catch (e) {
            const msg = e.response?.data?.message || "Erreur lors de la suppression";
            showToast(msg, { severity: "error" });
        }
    };

    const renderKeywords = (keywords, color) => {
        if (!keywords?.length) return <Typography variant="body2" color="text.disabled">—</Typography>;
        return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {keywords.map((kw) => (
                    <Chip key={kw} label={kw} size="small" color={color} variant="outlined" />
                ))}
            </Stack>
        );
    };

    const columns = [
        { field: "name", headerName: "Nom", renderCell: (row) => <strong>{row.name}</strong> },
        {
            field: "subjectPattern",
            headerName: "Pattern de sujet",
            renderCell: (row) => row.subjectPattern
                ? <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.subjectPattern}</Typography>
                : <Typography variant="body2" color="text.disabled">—</Typography>,
        },
        {
            field: "successKeywords",
            headerName: "Mots-clés succès",
            renderCell: (row) => renderKeywords(row.successKeywords, "success"),
        },
        {
            field: "failureKeywords",
            headerName: "Mots-clés échec",
            renderCell: (row) => renderKeywords(row.failureKeywords, "error"),
        },
        {
            field: "alertKeywords",
            headerName: "Mots-clés alerte",
            renderCell: (row) => renderKeywords(row.alertKeywords, "warning"),
        },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (row) => (
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Modifier" arrow>
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer" arrow>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];

    return (
        <div style={{ display: "flex", minHeight: "100vh" }}>
            <Sidebar />
            <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, width: "100%", overflowX: "hidden", display: "flex", flexDirection: "column" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        Types de sauvegarde
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                        Ajouter un type
                    </Button>
                </Stack>

                {loading ? (
                    <Typography color="text.secondary">Chargement...</Typography>
                ) : types.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                        Aucun type de sauvegarde configuré. Cliquez sur "Ajouter un type" pour commencer.
                    </Typography>
                ) : (
                    <GenericDataTable
                        columns={columns}
                        rows={types}
                        bordered
                        headerAlign="center"
                        cellAlign="center"
                        headerSx={{
                            fontSize: 14,
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                        }}
                        cellSx={{ fontSize: 13 }}
                    />
                )}
            </Box>

            <BackupTypeModal
                open={modalOpen}
                onClose={handleClose}
                onSave={handleSave}
                initialData={editData}
                loading={saving}
            />
        </div>
    );
}
